export interface Coordinates {
  lat: number;
  lng: number;
}

export interface AddressComponents {
  street: string;
  zip: string;
  city: string;
  country: string;
}

export class GeocodingService {
  private static apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  /**
   * Geocodes an address using Google Maps Geocoding API
   * @param addressComponents - The address components to geocode
   * @returns Promise<Coordinates | null> - The coordinates or null if geocoding failed
   */
  static async geocodeAddress(addressComponents: AddressComponents): Promise<Coordinates | null> {
    const { street, zip, city, country } = addressComponents;

    // Check if all required fields are present
    if (!street || !zip || !city || !country) {
      console.log(addressComponents);
      console.warn('Missing required address components for geocoding');
      return null;
    }

    // Check if API key is available
    if (!this.apiKey) {
      console.error('Google Maps API key is not configured');
      return null;
    }

    try {
      const address = `${street}, ${zip} ${city}, ${country}`;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${this.apiKey}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        return { lat, lng };
      } else {
        console.warn('Geocoding failed:', data.status, data.error_message);
        return null;
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      return null;
    }
  }

  /**
   * Validates if coordinates are valid (not 0,0 which is typically the default)
   * @param coordinates - The coordinates to validate
   * @returns boolean - True if coordinates are valid
   */
  static isValidCoordinates(coordinates: Coordinates): boolean {
    return coordinates.lat !== 0 && coordinates.lng !== 0;
  }

  /**
   * Formats an address for geocoding
   * @param addressComponents - The address components
   * @returns string - The formatted address
   */
  static formatAddress(addressComponents: AddressComponents): string {
    const { street, zip, city, country } = addressComponents;
    return `${street}, ${zip} ${city}, ${country}`;
  }
} 